import { useEffect, useState } from "react";
import { getAnswerTypeList } from "../../api/QuizAPI/AnswerTypeAPI";
import {
  addQuestion,
  getQuestionById,
  updateQuestion,
  updateQuestionOrder,
} from "../../api/QuizAPI/QuestionAPI";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import QuestionForm from "./QuestionForm";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import { deleteQuestion } from "../../api/QuizAPI/QuestionAPI";
import { Form } from "antd";
import { getQuestionDomainByQuestionId } from "../../api/QuizAPI/QuestionDomainOptionAPI";
import moment from "moment";
import {
  AnswerType,
  AnsweredBy,
  dateFormat,
} from "../common/GeneralConfigurations";

const ManageQuestionPage = ({
  index,
  question,
  groupId,
  setShowAddGroupButton,
  setShowGroupButtons,
  setShowPublishButton,
  onGetQuestionsByGroupId,
  questionsByGroupId,
  quiz,
  setAnswerBySwitch,
}) => {
  const [questionForm] = Form.useForm();

  const [questionGroup, setQuestionGroup] = useState({ ...question });
  const [answerTypeOptions, setAnswerTypeOptions] = useState([]);
  const [answerOption, setAnswerOption] = useState(null);
  const [showQuestionButtons, setShowQuestionButtons] = useState(false);
  const [choiceOptions, setChoiceOptions] = useState([]);
  const [questionDomainOptions, setQuestionDomainOptions] = useState([]);
  const [editQuestion, setEditQuestion] = useState(true);

  const questionNameValidation = [
    { required: true, message: "Question name is required" },
  ];
  const answerTypeValidation = [
    { required: true, message: "Answer type is required" },
  ];

  useEffect(() => {
    getAnswerTypes();

    if (question) {
      getQuestionDomainOptions(question.id);
      setEditQuestion(false);
    }
  }, []);

  const getAnswerTypes = async () => {
    await getAnswerTypeList()
      .then((res) => {
        if (res.status === 200) {
          setAnswerTypeOptions([...res.data]);

          if (question) {
            setShowPublishButton(true);
            setQuestionGroup({
              ...questionGroup,
              answerTypeId: res.data.find((x) => x.id === question.answerTypeId)
                .id,
            });
            setAnswerOption(
              res.data.find((x) => x.id === question.answerTypeId).name
            );
            setShowQuestionButtons(true);
          }
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Error retrieving answer types.");
      });
  };

  const getQuestionDomainOptions = async (questionId) => {
    await getQuestionDomainByQuestionId(questionId)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.length > 0) {
            const domainOptions = res.data.map((item) => {
              return {
                id: item.id,
                text: item.name,
              };
            });
            setQuestionDomainOptions(domainOptions);
            setChoiceOptions(domainOptions);
          } else {
            setQuestionDomainOptions([]);
          }
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Error retrieving question domain options.");
      });
  };

  const handleCancelQuestion = async (event) => {
    if (questionGroup.id) {
      await getQuestionById(event.id)
        .then((res) => {
          const { name, answerTypeId, minValue, maxValue, answeredBy } =
            res.data;
          if (res.status === 200) {
            if (
              answerTypeId === AnswerType.Data &&
              question.minValue != "" &&
              question.maxValue != ""
            ) {
              const transformedMinValue = moment(question.minValue, dateFormat);
              const transformedMaxValue = moment(question.maxValue, dateFormat);

              questionForm.setFieldsValue({
                name,
                answerTypeId,
                minValue: transformedMinValue,
                maxValue: transformedMaxValue,
                answeredBy,
              });
            } else {
              questionForm.setFieldsValue({
                name,
                answerTypeId,
                minValue,
                maxValue,
                answeredBy,
              });
            }
            setQuestionGroup({ ...questionGroup, answerTypeId: answerTypeId });
            questionForm.resetFields(["isRequired", "allowNotApplicable"]);
            getQuestionDomainOptions(event.id);
            AdnMessageSuccess("Question canceled with successs");
          } else {
            const errors = res.data.errors;

            errors.forEach((error) => {
              const errorMessage = error.message;
              AdnMessageError(errorMessage);
            });
          }
        })
        .catch(() => {
          AdnMessageError("Could not cancel question.");
        });
    } else {
      onGetQuestionsByGroupId(groupId);
      setShowQuestionButtons(true);
      setShowAddGroupButton(true);
      setShowGroupButtons(true);
    }
  };

  const handleSaveQuestion = async (event) => {
    questionGroup.id ? handleUpdateQuestion(event) : handleAddQuestion(event);
    setEditQuestion(false);
  };

  const handleAddQuestion = async (event) => {
    const choiceOptionsText = choiceOptions.map((item) => item.text);
    if (
      (event.answerTypeId === AnswerType.EscolhaUnica ||
        event.answerTypeId === AnswerType.EscolhaMultipla) &&
      choiceOptionsText.length < 2
    ) {
      AdnMessageError("List must have at least 2 items");
      return;
    }

    await addQuestion({
      groupId: groupId,
      answerTypeId: event.answerTypeId,
      name: event.name,
      choiceOptions: choiceOptionsText,
      minValue:
        event.answerTypeId === AnswerType.Data
          ? questionGroup.minValue
          : event.minValue === ""
            ? null
            : event.minValue,
      maxValue:
        event.answerTypeId === AnswerType.Data
          ? questionGroup.maxValue
          : event.maxValue === ""
            ? null
            : event.maxValue,
      isRequired: event.isRequired ? event.isRequired : false,
      allowNotApplicable: event.allowNotApplicable
        ? event.allowNotApplicable
        : false,
      answeredBy: quiz.isAnsweredByConsultant
        ? event.answeredBy
        : AnsweredBy.Supervisor,
      order: index,
    })
      .then((res) => {
        if (res.status === 200) {
          setShowQuestionButtons(true);
          setShowAddGroupButton(true);
          setShowGroupButtons(true);
          setShowPublishButton(true);
          setAnswerBySwitch(true);
          AdnMessageSuccess("Question added with success.");
          onGetQuestionsByGroupId(groupId);
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Could not add question.");
      });
  };

  const handleUpdateQuestion = async (event) => {
    if (
      (event.answerTypeId === AnswerType.EscolhaUnica ||
        event.answerTypeId === AnswerType.EscolhaMultipla) &&
      choiceOptions.length < 2
    ) {
      AdnMessageError("List must have at least 2 items");
      return;
    }

    await updateQuestion({
      id: event.id,
      groupId: groupId,
      answerTypeId: event.answerTypeId,
      name: event.name,
      choiceOptions: choiceOptions,
      minValue:
        event.answerTypeId === AnswerType.Data
          ? questionGroup.minValue
          : event.minValue === ""
            ? null
            : event.minValue,
      maxValue:
        event.answerTypeId === AnswerType.Data
          ? questionGroup.maxValue
          : event.maxValue === ""
            ? null
            : event.maxValue,
      isRequired: event.isRequired,
      allowNotApplicable: event.allowNotApplicable,
      answeredBy: quiz.isAnsweredByConsultant
        ? event.answeredBy
        : AnsweredBy.Supervisor,
      order: index,
    })
      .then((res) => {
        if (res.status === 200) {
          AdnMessageSuccess("Question updated with success.");
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
        setEditQuestion(false);
      })
      .catch(() => {
        AdnMessageError("Could not update question.");
      });
  };

  const handleQuestionNameChange = (value) => {
    setQuestionGroup({ ...questionGroup, description: value.target.value });
  };

  const handleAddChoices = (event) => {
    const choiceOptions = event.map((item) => ({
      id: item.id,
      text: item.text,
    }));
    setChoiceOptions(choiceOptions);
  };

  const handleQuestionAnswerTypeChange = (value) => {
    setQuestionGroup({ ...questionGroup, answerTypeId: value });
    setAnswerOption(answerTypeOptions.find((x) => x.id === value).name);
    questionForm.setFieldValue("minValue", "");
    questionForm.setFieldValue("maxValue", "");
    if (
      value !== AnswerType.EscolhaUnica &&
      value !== AnswerType.EscolhaMultipla
    ) {
      setQuestionDomainOptions([]);
    }
  };

  const handleChangeAnsweredBy = (value) => {
    setQuestionGroup({ ...questionGroup, answeredBy: value });
  };

  const handleQuestionMaxValue = (event) => {
    setQuestionGroup({ ...questionGroup, maxValue: event.target.value });
  };

  const handleQuestionMinValue = (event) => {
    setQuestionGroup({ ...questionGroup, minValue: event.target.value });
  };

  const handleQuestionMaxValueDatePicker = (date, dateString) => {
    setQuestionGroup({ ...questionGroup, maxValue: dateString });
  };

  const handleQuestionMinValueDatePicker = (date, dateString) => {
    setQuestionGroup({ ...questionGroup, minValue: dateString });
  };

  const handleDeleteQuestion = async (event) => {
    await deleteQuestion(event.id)
      .then((res) => {
        if (res.status === 200) {
          onGetQuestionsByGroupId(event.groupId, true);
          AdnMessageSuccess("Question deleted with success.");
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Can't delete question.");
      });
  };

  const handleMoveUp = async (event) => {
    await updateQuestionOrder({
      id: event.id,
      sortDirection: "-",
    })
      .then((res) => {
        if (res.status === 200) {
          onGetQuestionsByGroupId(event.groupId);
          AdnMessageSuccess("Order of question changed with success.");
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Could not change the order of the question.");
      });
  };

  const handleMoveDown = async (event) => {
    await updateQuestionOrder({
      id: event.id,
      sortDirection: "+",
    })
      .then((res) => {
        if (res.status === 200) {
          onGetQuestionsByGroupId(event.groupId);
          AdnMessageSuccess("Order of question changed with success.");
        } else {
          const errors = res.data.errors;

          errors.forEach((error) => {
            const errorMessage = error.message;
            AdnMessageError(errorMessage);
          });
        }
      })
      .catch(() => {
        AdnMessageError("Could not change the order of the question.");
      });
  };

  return (
    <>
      <QuestionForm
        index={index}
        questionNameValidation={questionNameValidation}
        answerTypeValidation={answerTypeValidation}
        handleDeleteQuestion={handleDeleteQuestion}
        handleCancelQuestion={handleCancelQuestion}
        handleSaveQuestion={handleSaveQuestion}
        handleMoveUp={handleMoveUp}
        handleMoveDown={handleMoveDown}
        handleQuestionNameChange={handleQuestionNameChange}
        handleQuestionAnswerTypeChange={handleQuestionAnswerTypeChange}
        handleQuestionMaxValue={handleQuestionMaxValue}
        handleQuestionMinValue={handleQuestionMinValue}
        questionGroup={questionGroup}
        answerTypeOptions={answerTypeOptions}
        answerOption={answerOption}
        showQuestionButtons={showQuestionButtons}
        questionForm={questionForm}
        questionsByGroupId={questionsByGroupId}
        handleAddChoices={handleAddChoices}
        choiceOptions={choiceOptions}
        questionDomainOptions={questionDomainOptions}
        quiz={quiz}
        handleChangeAnsweredBy={handleChangeAnsweredBy}
        question={question}
        handleQuestionMinValueDatePicker={handleQuestionMinValueDatePicker}
        handleQuestionMaxValueDatePicker={handleQuestionMaxValueDatePicker}
        editQuestion={editQuestion}
        setEditQuestion={setEditQuestion}
        setQuestionGroup={setQuestionGroup}
      />
    </>
  );
};

export default ManageQuestionPage;
